import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DriverDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    assigned: 0,
    inProgress: 0,
    delivered: 0,
    earnings: 0
  });
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/orders/driver/orders').catch(() => ({ data: { data: [] } }));
      const orders = data.data || [];
      
      setStats({
        assigned: orders.length,
        inProgress: orders.filter(o => o.status === 'en_camino').length,
        delivered: orders.filter(o => o.status === 'entregado').length,
        earnings: orders.filter(o => o.status === 'entregado').reduce((s, o) => s + Number(o.delivery_fee_usd || 0), 0)
      });
      
      setActiveOrders(orders.filter(o => o.status === 'en_camino' || o.status === 'aceptado').slice(0, 5));
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, []);

  const cards = [
    { label: 'Asignados', value: stats.assigned, icon: '📋', color: 'from-blue-500 to-blue-600' },
    { label: 'En camino', value: stats.inProgress, icon: '🚗', color: 'from-orange-500 to-orange-600' },
    { label: 'Entregados', value: stats.delivered, icon: '✅', color: 'from-green-500 to-green-600' },
    { label: 'Ganancias', value: `$${stats.earnings.toFixed(2)}`, icon: '💰', color: 'from-gold to-gold-dark' }
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold">Hola, {user?.name?.split(' ')[0] || 'Motorizado'} 👋</h1>
        <p className="text-gray-500">Bienvenido a tu panel de entregas</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card, i) => (
          <div key={i} className={`bg-gradient-to-br ${card.color} text-white p-5 rounded-2xl shadow-lg`}>
            <div className="text-3xl mb-2">{card.icon}</div>
            <div className="text-sm opacity-90">{card.label}</div>
            <div className="text-2xl font-bold mt-1">{card.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">🔥 Entregas activas</h2>
          <Link to="/driver/pedidos" className="text-gold text-sm hover:underline">Ver todas →</Link>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-6xl mb-3">🎉</div>
            <p className="font-medium">No tienes entregas pendientes</p>
            <p className="text-sm mt-1">Relájate, te avisaremos cuando haya nuevos pedidos</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeOrders.map(order => (
              <Link
                key={order.id}
                to={`/driver/pedidos`}
                className="block p-4 bg-background-alt rounded-xl hover:bg-gold/10 transition"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold">#{order.order_number}</div>
                  <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">En camino</span>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  📍 {order.customer_info?.address}
                </div>
                <div className="text-xs text-gray-500">
                  {order.customer_info?.name} · {order.customer_info?.phone}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
